import React, { useState, useEffect } from 'react';
import { annuitySchemeList, annuityFrequencyList } from '../pages/Exit/Stepper/data';
import CustomDropdown from '../pages/Exit/Stepper/CustomDropdown';
import CustomDatePicker from '../pages/Exit/Stepper/CustomDatePicker';
import AnnuitySchemeDropdown from '../pages/Exit/Stepper/AnnuitySchemeDropdown';
import { calculateAge, validateWithdrawalPR } from '../pages/Exit/Stepper/ValidationUtils';
import { getBankPensionData, IMAGE_URL } from '../Api/commonAPI';
import { decryptData } from '../Decrypt/Decrypt';

const PensionPlusCalculator = ({ onClose }) => {
  // Helper function to get current date in DD-MM-YYYY format
  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Helper function to format number with Indian numbering system
  const formatIndianNumber = (value) => {
    if (value === null || value === undefined || value === '' || value === 0) {
      return '';
    }

    // Convert to number first to handle .00 cases
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return '';
    }

    // If it's a whole number, format without decimals
    const isWholeNumber = numValue % 1 === 0;
    const strValue = isWholeNumber ? numValue.toString() : numValue.toFixed(2);

    if (strValue === '' || strValue === '0') {
      return '';
    }

    const isNegative = strValue.startsWith('-');
    const cleanValue = isNegative ? strValue.substring(1) : strValue;

    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    let decimalPart = parts.length > 1 ? '.' + parts[1] : '';

    // Remove trailing zeros from decimal part
    if (decimalPart) {
      decimalPart = decimalPart.replace(/0+$/, '');
      if (decimalPart === '.') {
        decimalPart = '';
      }
    }

    let formattedInteger = '';
    const length = integerPart.length;

    if (length <= 3) {
      formattedInteger = integerPart;
    } else {
      const lastThree = integerPart.substring(length - 3);
      const remaining = integerPart.substring(0, length - 3);
      const remainingFormatted = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ',');
      formattedInteger = remainingFormatted + ',' + lastThree;
    }

    return (isNegative ? '-' : '') + formattedInteger + decimalPart;
  };

  // Helper function to format number with commas
  const formatNumberWithCommas = (value) => {
    return formatIndianNumber(value);
  };

  // Helper function to format number without unnecessary .00
  const formatNumber = (value) => {
    if (!value && value !== 0) return '0';
    const num = parseFloat(value);
    if (isNaN(num)) return '0';
    // If it's a whole number, return without decimals, otherwise return with 2 decimals
    return num % 1 === 0 ? num.toString() : num.toFixed(2);
  };

  const [formData, setFormData] = useState({
    WorkSectorName: '',
    WorkSectorDOB: getCurrentDate(),
    NPSCorpus: '',
    WithdrawalPR: '',
    WithdrawalAmount: '',
    AnnuityValue: '',
    AnnuitySchemeName: '',
    AnnuityFrequencySchemeName: ''
  });

  const [currentAge, setCurrentAge] = useState(() => {
    return calculateAge(getCurrentDate());
  });

  const [bankList, setBankList] = useState([]);
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);

  // Validation errors state
  const [errors, setErrors] = useState({
    WithdrawalPR: null
  });

  const workSectorList = [
    'Central Goverment',
    'State Government',
    'Private Sector',
    'Public Sector'
  ];

  const handleDatePickerChange = (e) => {
    const dateValue = e.target.value; // Format from CustomDatePicker: DD-MM-YYYY

    if (dateValue) {
      setFormData(prev => ({
        ...prev,
        WorkSectorDOB: dateValue
      }));

      const age = calculateAge(dateValue);
      setCurrentAge(age);
    } else {
      setFormData(prev => ({
        ...prev,
        WorkSectorDOB: ''
      }));
      setCurrentAge(0);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Handle NPSCorpus - format with commas for display, store numeric value
    if (name === 'NPSCorpus') {
      if (value === '' || value === null) {
        setFormData(prev => ({
          ...prev,
          [name]: ''
        }));
        return;
      } else {
        // Remove all non-digit characters (except decimal point if needed)
        const numericValue = value.replace(/[^\d]/g, '');
        if (numericValue === '') {
          setFormData(prev => ({
            ...prev,
            [name]: ''
          }));
          return;
        } else {
          // Store the numeric value (without commas) in state for calculations
          setFormData(prev => ({
            ...prev,
            [name]: numericValue
          }));
          return;
        }
      }
    }

    // Validate WithdrawalPR - max 20
    if (name === 'WithdrawalPR') {
      if (value === '' || value === null) {
        setFormData(prev => ({
          ...prev,
          [name]: ''
        }));
        const error = validateWithdrawalPR('');
        setErrors(prev => ({
          ...prev,
          [name]: error
        }));
        return;
      } else {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          setFormData(prev => ({
            ...prev,
            [name]: value
          }));

          if (numValue > 20) {
            setErrors(prev => ({
              ...prev,
              [name]: 'Withdrawal % cannot exceed 20%.'
            }));
          } else {
            const error = validateWithdrawalPR(value);
            setErrors(prev => ({
              ...prev,
              [name]: error
            }));
          }
          return;
        }
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Calculate withdrawal amount and annuity value when NPSCorpus and WithdrawalPR change
  useEffect(() => {
    const withdrawalPercent = parseFloat(formData.WithdrawalPR) || 0;
    if (formData.NPSCorpus && formData.WithdrawalPR && withdrawalPercent <= 20) {
      const corpus = parseFloat(formData.NPSCorpus) || 0;
      const withdrawalAmount = (corpus * withdrawalPercent) / 100;
      const annuityValue = corpus - withdrawalAmount;

      setFormData(prev => ({
        ...prev,
        WithdrawalAmount: withdrawalAmount.toFixed(2),
        AnnuityValue: annuityValue.toFixed(2)
      }));
    } else if (formData.NPSCorpus && !formData.WithdrawalPR) {
      setFormData(prev => ({
        ...prev,
        WithdrawalAmount: '',
        AnnuityValue: ''
      }));
    } else if (!formData.NPSCorpus || !formData.WithdrawalPR || withdrawalPercent > 20) {
      setFormData(prev => ({
        ...prev,
        WithdrawalAmount: '',
        AnnuityValue: ''
      }));
    }
  }, [formData.NPSCorpus, formData.WithdrawalPR]);

  // Fetch bank pension data when required fields change
  useEffect(() => {
    const fetchBankData = async () => {
      // Only fetch if all required fields are filled
      if (!currentAge || !formData.AnnuityValue || !formData.AnnuityFrequencySchemeName || !formData.AnnuitySchemeName) {
        setBankList([]);
        setSelectedBanks([]);
        return;
      }

      setLoadingBanks(true);
      try {
        const payload = {
          Age: currentAge,
          Amount: parseFloat(formData.AnnuityValue) || 0,
          AnnuityFrequency: formData.AnnuityFrequencySchemeName,
          AnnuityScheme: formData.AnnuitySchemeName,
          AnnuityValue: formData.AnnuityValue
        };

        const response = await getBankPensionData(payload);

        // Decrypt the response data
        if (response?.data?.data) {
          try {
            const decryptedData = decryptData(response.data.data);

            if (Array.isArray(decryptedData)) {
              setBankList(decryptedData);
            } else if (decryptedData && typeof decryptedData === 'object') {
              if (Array.isArray(decryptedData.data)) {
                setBankList(decryptedData.data);
              } else if (Array.isArray(decryptedData.BankList)) {
                setBankList(decryptedData.BankList);
              } else {
                setBankList([]);
              }
            } else {
              setBankList([]);
            }
          } catch (decryptError) {
            console.error('Error decrypting bank data:', decryptError);
            setBankList([]);
          }
        } else {
          setBankList([]);
        }
      } catch (error) {
        console.error('Error fetching bank data:', error);
        setBankList([]);
      } finally {
        setLoadingBanks(false);
      }
    };

    fetchBankData();
  }, [currentAge, formData.AnnuityValue, formData.AnnuityFrequencySchemeName, formData.AnnuitySchemeName]);

  const handleBankCheckboxChange = (bankId) => {
    setSelectedBanks(prev => {
      const isCurrentlySelected = prev.includes(bankId);
      return isCurrentlySelected
        ? prev.filter(id => id !== bankId) // Remove if already selected
        : [bankId]; // Only allow single selection
    });
  };

  // Inline styles
  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: '90vh',
    background: '#ffffff',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)'
  };

  const headerStyle = {
    position: 'sticky',
    top: 0,
    zIndex: 10,
    background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
    padding: '1.5rem 2rem',
    borderBottom: 'none',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
  };

  const headerContentStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem'
  };

  const titleStyle = {
    margin: 0,
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#ffffff',
    letterSpacing: '-0.02em'
  };

  const contentStyle = {
    flex: 1,
    overflowY: 'auto',
    padding: '2rem',
    background: '#f8f9fa'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1.5rem'
  };

  const fieldStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  };

  const labelStyle = {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: '#495057',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.75rem 1rem',
    fontSize: '0.95rem',
    color: '#212529',
    backgroundColor: '#ffffff',
    border: '1.5px solid #dee2e6',
    borderRadius: '8px',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit'
  };

  const inputReadonlyStyle = {
    ...inputStyle,
    backgroundColor: '#f8f9fa',
    color: '#6c757d',
    cursor: 'not-allowed',
    borderColor: '#e9ecef'
  };

  const ageInfoStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem',
    color: '#0d6efd',
    fontWeight: 500,
    marginTop: '0.25rem'
  };

  return (
    <>
      <style>
        {`
          @media (max-width: 768px) {
            .pension-calculator-grid {
              grid-template-columns: 1fr !important;
              gap: 1.25rem !important;
            }
            .pension-calculator-content {
              padding: 1.5rem !important;
            }
            .pension-calculator-header {
              padding: 1.25rem 1.5rem !important;
            }
            .pension-calculator-title {
              font-size: 1.25rem !important;
            }
          }
          @media (max-width: 480px) {
            .pension-calculator-content {
              padding: 1rem !important;
            }
            .pension-calculator-header {
              padding: 1rem !important;
            }
            .pension-calculator-title {
              font-size: 1.1rem !important;
            }
          }
        `}
      </style>
      <div style={containerStyle}>
        {/* Sticky Header */}
        <div style={headerStyle} className="pension-calculator-header">
          <div style={headerContentStyle}>
            <i className="fas fa-calculator" style={{ fontSize: '1.5rem', color: '#ffffff' }}></i>
            <h2 style={titleStyle} className="pension-calculator-title">PensionPlus Calculator</h2>
          </div>
        </div>

        {/* Scrollable Content Area */}
        <div style={contentStyle} className="pension-calculator-content">
          <div style={gridStyle} className="pension-calculator-grid">
            {/* Work Sector */}
            <div style={fieldStyle}>
              <label htmlFor="WorkSectorName" style={labelStyle}>
                Work Sector
              </label>
              <CustomDropdown
                options={workSectorList}
                value={formData.WorkSectorName}
                onChange={handleSelectChange}
                placeholder="Select work sector..."
                name="WorkSectorName"
              />
            </div>

            {/* Subscriber's Date of Birth */}
            <div style={fieldStyle}>
              <label htmlFor="WorkSectorDOB" style={labelStyle}>
                Subscriber's Date of Birth
              </label>
              <CustomDatePicker
                name="WorkSectorDOB"
                value={formData.WorkSectorDOB}
                onChange={handleDatePickerChange}
                maxDate={new Date()}
                placeholder="DD-MM-YYYY"
              />
              {currentAge > 0 && (
                <div style={ageInfoStyle}>
                  <i className="fas fa-info-circle"></i>
                  <span>Your current age: {currentAge} years</span>
                </div>
              )}
            </div>

            {/* NPS Corpus */}
            <div style={fieldStyle}>
              <label htmlFor="NPSCorpus" style={labelStyle}>
                NPS Corpus
              </label>
              <input
                type="text"
                style={inputStyle}
                id="NPSCorpus"
                name="NPSCorpus"
                value={formData.NPSCorpus ? formatNumberWithCommas(formData.NPSCorpus) : ''}
                onChange={handleInputChange}
                placeholder="Enter NPS corpus amount (e.g., 10,000)"
                onFocus={(e) => {
                  e.target.style.borderColor = '#0d6efd';
                  e.target.style.boxShadow = '0 0 0 3px rgba(13, 110, 253, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#dee2e6';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            {/* Withdrawal Percentage */}
            <div style={fieldStyle}>
              <label htmlFor="WithdrawalPR" style={labelStyle}>
                Withdrawal Percentage <span style={{ color: '#dc3545', fontWeight: 600 }}>*</span>
              </label>
              <input
                type="number"
                style={{
                  ...inputStyle,
                  borderColor: errors.WithdrawalPR ? '#dc3545' : inputStyle.borderColor
                }}
                id="WithdrawalPR"
                name="WithdrawalPR"
                value={formData.WithdrawalPR}
                onChange={handleInputChange}
                placeholder="Enter withdrawal percentage (max 20%)"
                min="0"
                max="20"
                step="1"
                onWheel={(e) => e.target.blur()}
                onFocus={(e) => {
                  e.target.style.borderColor = errors.WithdrawalPR ? '#dc3545' : '#0d6efd';
                  e.target.style.boxShadow = errors.WithdrawalPR
                    ? '0 0 0 3px rgba(220, 53, 69, 0.1)'
                    : '0 0 0 3px rgba(13, 110, 253, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.WithdrawalPR ? '#dc3545' : '#dee2e6';
                  e.target.style.boxShadow = 'none';
                }}
              />
              {errors.WithdrawalPR && (
                <div style={{
                  fontSize: '0.875rem',
                  color: '#dc3545',
                  marginTop: '0.25rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.25rem'
                }}>
                  <i className="fas fa-exclamation-circle"></i>
                  <span>{errors.WithdrawalPR}</span>
                </div>
              )}
            </div>

            {/* Withdrawal Amount */}
            <div style={fieldStyle}>
              <label htmlFor="WithdrawalAmount" style={labelStyle}>
                Withdrawal Amount
              </label>
              <input
                type="text"
                style={inputReadonlyStyle}
                id="WithdrawalAmount"
                name="WithdrawalAmount"
                value={formData.WithdrawalAmount ? formatIndianNumber(parseFloat(formData.WithdrawalAmount)) : ''}
                onChange={handleInputChange}
                placeholder="Withdrawal amount"
                readOnly
                tabIndex="-1"
              />
            </div>

            {/* Annuity Value */}
            <div style={fieldStyle}>
              <label htmlFor="AnnuityValue" style={labelStyle}>
                Annuity Value
              </label>
              <input
                type="text"
                style={inputReadonlyStyle}
                id="AnnuityValue"
                name="AnnuityValue"
                value={formData.AnnuityValue ? formatIndianNumber(parseFloat(formData.AnnuityValue)) : ''}
                onChange={handleInputChange}
                placeholder="Annuity value"
                readOnly
                tabIndex="-1"
              />
            </div>

            {/* Select Annuity Scheme */}
            <div style={fieldStyle}>
              <label htmlFor="AnnuitySchemeName" style={labelStyle}>
                Select Annuity Scheme
              </label>
              <AnnuitySchemeDropdown
                options={annuitySchemeList}
                value={formData.AnnuitySchemeName}
                onChange={handleSelectChange}
                placeholder="Select annuity scheme..."
                name="AnnuitySchemeName"
              />
            </div>

            {/* Select Annuity Frequency */}
            <div style={fieldStyle}>
              <label htmlFor="AnnuityFrequencySchemeName" style={labelStyle}>
                Select Annuity Frequency
              </label>
              <CustomDropdown
                options={annuityFrequencyList}
                value={formData.AnnuityFrequencySchemeName}
                onChange={handleSelectChange}
                placeholder="Select annuity frequency..."
                name="AnnuityFrequencySchemeName"
              />
            </div>
          </div>

          {/* Bank Listing Section */}
          {formData.AnnuityValue && formData.AnnuityFrequencySchemeName && formData.AnnuitySchemeName && (
            <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '2px solid #e9ecef' }}>
              {loadingBanks ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p style={{ marginTop: '1rem', color: '#6c757d', fontSize: '0.9rem' }}>Loading bank list...</p>
                </div>
              ) : Array.isArray(bankList) && bankList.length > 0 ? (
                <div>
                  <h6 style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: '#000',
                    marginBottom: '1.5rem'
                  }}>
                    Select Annuity Service Providers
                  </h6>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                    gap: '1rem'
                  }}>
                    {bankList.map((bank, index) => {
                      const bankId = bank.BankMasterID || bank.BankID || index;
                      const bankName = bank.BankName || bank.Name || 'Unknown Bank';
                      const bankLogo = bank.BankLogo || bank.Logo || '';
                      const pensionData = bank.BankPensionData || bank.PensionData || [];
                      const amount = pensionData[0]?.Amount || pensionData?.Amount || '0.00';
                      const percentage = pensionData[0]?.PensionPer || pensionData?.PensionPer || pensionData[0]?.Percentage || '0.0';
                      const isSelected = selectedBanks.includes(bankId);

                      return (
                        <div
                          key={bankId}
                          onClick={() => handleBankCheckboxChange(bankId)}
                          style={{
                            backgroundColor: '#e7f1ff',
                            padding: '0.75rem 1rem',
                            borderRadius: '10px',
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            border: isSelected ? '2px solid #0d6efd' : '1px solid #cfe2ff',
                            boxShadow: isSelected ? '0 4px 8px rgba(13, 110, 253, 0.2)' : '0 2px 4px rgba(0,0,0,0.1)'
                          }}
                        >
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}>
                            {/* Left: Logo and Bank Name */}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              flex: 1,
                              minWidth: 0
                            }}>
                              {bankLogo && (
                                <img
                                  src={bankLogo.startsWith('http') ? bankLogo : `${IMAGE_URL}${bankLogo}`}
                                  alt={bankName}
                                  style={{
                                    width: '35px',
                                    height: '35px',
                                    objectFit: 'contain',
                                    marginRight: '10px',
                                    borderRadius: '4px',
                                    flexShrink: 0
                                  }}
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{
                                  fontSize: '0.875rem',
                                  fontWeight: '600',
                                  color: '#333',
                                  marginBottom: '0',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis'
                                }}>
                                  {bankName}
                                </div>
                              </div>
                            </div>

                            {/* Middle: Selection Indicator */}
                            <div style={{ margin: '0 0.5rem', flexShrink: 0 }}>
                              <div
                                style={{
                                  width: '24px',
                                  height: '24px',
                                  borderRadius: '6px',
                                  backgroundColor: isSelected ? '#0d6efd' : 'transparent',
                                  border: isSelected ? 'none' : '2px solid #dee2e6',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                {isSelected && (
                                  <i
                                    className="fas fa-check"
                                    style={{
                                      color: 'white',
                                      fontSize: '0.75rem',
                                      fontWeight: 'bold'
                                    }}
                                  ></i>
                                )}
                              </div>
                            </div>

                            {/* Right: Financial Details */}
                            <div style={{
                              textAlign: 'right',
                              minWidth: '90px',
                              flexShrink: 0
                            }}>
                              <div style={{
                                fontSize: '0.875rem',
                                color: '#000',
                                fontWeight: '600',
                                marginBottom: '2px',
                                lineHeight: '1.2'
                              }}>
                                ₹ {formatNumber(amount)}
                              </div>
                              <div style={{
                                fontSize: '0.95rem',
                                color: '#28a745',
                                fontWeight: '700',
                                lineHeight: '1.2'
                              }}>
                                {formatNumber(percentage)}%
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: '2rem',
                  color: '#6c757d',
                  fontSize: '0.9rem'
                }}>
                  {formData.AnnuityValue && formData.AnnuityFrequencySchemeName && formData.AnnuitySchemeName && currentAge
                    ? 'No banks available for the selected criteria'
                    : 'Please fill all required fields to see bank list'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PensionPlusCalculator;
